import * as React from 'react';
import { useCallback, useState } from 'react';

import { type ApolloCache, gql } from '@apollo/client';
import { useLazyQuery, useMutation } from '@apollo/client/react';

import { ImageGalleryDialog } from '@modules/_shared/components/image-upload/components/ImageGalleryDialog';
import RASnackbar from '@modules/_shared/components/RASnackbar';
import { INSERT_PROPERTY_IMAGE } from '@modules/listings/ListingPropertyImages/listingPropertyQueries';
import { updatePropertyImagesCountCache } from '@modules/listings/ListingPropertyImages/propertyImageCache';

const GET_MAX_ORDER_NUMBER = gql(/* GraphQL */ `
  query GetMaxOrderNumber($propertyId: uuid!) {
    property_images_aggregate(where: { property_id: { _eq: $propertyId } }) {
      aggregate {
        max {
          order_nr
        }
      }
    }
  }
`);

type PropertyImageUploadModalProps = {
  propertyId: string;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

type GetMaxOrderNumberQuery = {
  property_images_aggregate?: {
    aggregate?: {
      max?: {
        order_nr?: number | null;
      } | null;
    } | null;
  };
};

export const PropertyImageUploadModal = React.memo(
  ({ propertyId, open, onClose, onSuccess }: PropertyImageUploadModalProps) => {
    const [error, setError] = useState<string | null>(null);

    const [getMaxOrderNumber] = useLazyQuery<GetMaxOrderNumberQuery>(
      GET_MAX_ORDER_NUMBER,
      { fetchPolicy: 'network-only' },
    );

    const [insertImage] = useMutation(INSERT_PROPERTY_IMAGE, {
      update: (cache: ApolloCache, { data }) => {
        if (!data?.insert_property_images_one) {
          return;
        }

        const newImage = data.insert_property_images_one;
        updatePropertyImagesCountCache(cache, propertyId, 1);

        const propertyIdentifier = cache.identify({
          __typename: 'properties',
          id: propertyId,
        });

        if (propertyIdentifier) {
          try {
            cache.modify({
              id: propertyIdentifier,
              fields: {
                property_images(existingImages = []) {
                  const newImageRef = cache.writeFragment({
                    data: newImage,
                    fragment: gql`
                      fragment NewPropertyImage on property_images {
                        __typename
                        id
                        order_nr
                        image {
                          __typename
                          id
                          url
                        }
                      }
                    `,
                  });
                  return [...existingImages, newImageRef];
                },
              },
            });
          } catch (error) {
            console.warn('Failed to update property cache:', error);
          }
        }
      },
    });

    const handleClose = useCallback(() => {
      onClose();
    }, [onClose]);

    const calculateNextOrderNumber = useCallback(async () => {
      const { data } = await getMaxOrderNumber({
        variables: { propertyId },
      });
      const maxOrder =
        data?.property_images_aggregate?.aggregate?.max?.order_nr;
      return maxOrder != null ? maxOrder + 1 : 0;
    }, [getMaxOrderNumber, propertyId]);

    const handleFilesUploaded = useCallback(
      async (uploadedImages: { id: string; url: string }[]) => {
        try {
          const startingOrder = await calculateNextOrderNumber();

          const uploadPromises = uploadedImages.map((image, index) =>
            insertImage({
              variables: {
                object: {
                  property_id: propertyId,
                  image: {
                    data: { url: image.url },
                  },
                  order_nr: startingOrder + index,
                },
              },
            }),
          );

          await Promise.all(uploadPromises);
          onSuccess?.();
          handleClose();
        } catch (error) {
          setError(
            error instanceof Error ? error.message : 'Failed to save images',
          );
        }
      },
      [
        insertImage,
        propertyId,
        onSuccess,
        calculateNextOrderNumber,
        handleClose,
      ],
    );

    return (
      <>
        <ImageGalleryDialog
          open={open}
          onClose={handleClose}
          entityId={propertyId}
          entityType="property"
          onSelectImage={() => Promise.resolve()}
          onFilesUploaded={handleFilesUploaded}
        />

        {error && <RASnackbar message={error} onClose={() => setError(null)} />}
      </>
    );
  },
);
